import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { api } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { apiError, fileHref } from './salesShared';
import { isVideoFile, isVideoUrl } from '../../lib/videos';
import { uploadVideo } from './videoUpload';
import { VideoLink } from './VideoPlayer';

export const MAX_FILES = 10;
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.mp4,.mov,.m4v,.webm,.avi,.mkv';

// Uploads the chosen files one after the other. Those that work are kept even
// if another fails. Videos go straight to Bunny and report their progress to
// onProgress({ name, percent }). Returns { uploaded: [{ url, name }], error }.
export const uploadFiles = async (chosen, alreadyAttached, onProgress) => {
  const room = Math.max(0, MAX_FILES - alreadyAttached);
  const uploaded = [];
  const problems = [];
  for (const file of chosen.slice(0, room)) {
    try {
      const up = isVideoFile(file.name)
        ? await uploadVideo(file, (percent) => onProgress?.({ name: file.name, percent }))
        : await api.upload('/sales/attachments', file);
      uploaded.push({ url: up.url, name: up.name });
    } catch (e) {
      problems.push(`${file.name}: ${apiError(e, 'upload failed')}`);
    }
  }
  if (chosen.length > room) problems.push(`Only ${MAX_FILES} files fit on one activity.`);
  return { uploaded, error: problems.join(' ') };
};

// The files on an activity, each removable, and a button that adds one or
// several at a time.
export const FilesEditor = ({ files, onAdd, onRemove, busy = false, compact = false }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const pick = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = '';
    if (!chosen.length) return;
    try {
      await onAdd(chosen, setProgress);
    } finally {
      setProgress(null);
    }
  };
  // Leaving the page would lose a video on its way to Bunny.
  useEffect(() => {
    if (!progress) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [progress]);
  const remove = (i) => {
    const video = isVideoUrl(files[i].url);
    if (video && !window.confirm('Remove this video? It will be permanently deleted once the change is saved.')) return;
    onRemove(i);
  };
  return (
    <div className="space-y-1.5" data-testid="files-editor">
      <input ref={ref} type="file" multiple className="hidden" onChange={pick} accept={ACCEPT} data-testid="files-input" />
      {files.map((f, i) => (
        <div key={f.url} className={`flex items-center justify-between gap-2 ${compact ? '' : 'p-2 bg-[#0A0A0A] border border-[#1E293B] rounded-md'}`} data-testid={`file-row-${i}`}>
          {isVideoUrl(f.url) ? (
            <VideoLink file={f} className="flex items-center gap-2 text-sm text-[#60A5FA] hover:underline min-w-0" testId={`file-video-${i}`} />
          ) : (
            <a href={fileHref(f.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#60A5FA] hover:underline min-w-0">
              <Paperclip className="w-4 h-4 shrink-0" />
              <span className="truncate" data-testid={`file-name-${i}`}>{f.name || 'File'}</span>
            </a>
          )}
          <button type="button" disabled={busy} onClick={() => remove(i)} className="text-[#94A3B8] hover:text-red-400 shrink-0 disabled:opacity-50" aria-label={`Remove ${f.name || 'file'}`} data-testid={`file-remove-${i}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {progress && (
        <div className="space-y-1" data-testid="video-progress">
          <div className="flex justify-between gap-2 text-xs text-[#94A3B8]">
            <span className="truncate">Uploading {progress.name}. Keep this page open.</span>
            <span>{progress.percent}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#1E293B] overflow-hidden">
            <div className="h-full bg-[#3B82F6] transition-all" style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
      )}
      {files.length < MAX_FILES && (
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]" data-testid="files-add-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1" />}
          {busy ? 'Uploading…' : 'Add files'}
        </Button>
      )}
    </div>
  );
};

export default FilesEditor;
